import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
    Actions_,
    Alert_,
    AlertContext,
    Builder_,
    Dropdown_,
    FormBox,
    FormBoxTitle,
    Grey_,
    Input_,
    Main_,
    PageAlwaysVisible,
    PageSidebar,
    PageTitle,
    Textarea_
} from "monica-alexandria";
import store from "../../../redux/store";
import { alertMessage } from "../../../helpers/messagesAlerts";
import { add_prospect } from "../../../services/api/Prospects";
import { Toolbar_ } from "../../partials/navigation/Toolbar_.jsx";
import { Location_Input } from "../../../components/location/Location_Input";
import { get_business_categories } from "./utils/business_categories";
import { read_brand_input_value } from "../../../utils/input/brand_input";

const NewLead = styled.div`
   
`;

const NewLeadContent = styled.div`
    
`;

const initialLead = {
    firstname: "",
    lastname: "",
    emails: [],
    phones: [],
    brand: "",
    category: "",
    subcategory: "",
    locations: [],
    platformSource: "",
    extraInfo: ""
};

const initialMessages = {
    successes: [],
    helpers: [],
    errors: []
};

/**
 * Splits one visible full-name input into backend first and last name fields.
 */
const read_name_parts = (firstname, lastname) => {
    return {
        firstname: firstname.trim(),
        lastname: lastname.trim()
    };
};

/**
 * Reads one action note from the extra info box for the add-lead API.
 */
const read_extra_info_actions = (extraInfo, submitterFirstName) => {
    if (!extraInfo.trim()) {
        return [];
    }

    return [
        {
            id: Date.now(),
            action: "Note",
            schedule: false,
            comment: extraInfo.trim(),
            created: Date.now(),
            seller: submitterFirstName,
            sellerName: submitterFirstName
        }
    ];
};

/**
 * Reads the visible submitter source shown on Leads and Brand cards.
 */
const read_submitter_source = (user) => {
    const username = `${user?.email || user?.username || ''}`.trim();

    if (!username.includes('@')) {
        return username;
    }

    return username.split('@')[0].trim();
};

/**
 * Includes an unfinished list input value when the user saves directly.
 */
const read_list_with_pending_value = (list, value) => {
    const cleanValue = value.trim();

    if (!cleanValue) {
        return list;
    }

    return [...list, cleanValue];
};

/**
 * Checks whether the entered email can be added to the lead list.
 */
const read_email_is_valid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(email);
};

/**
 * Checks whether the entered phone can be added to the lead list.
 */
const read_phone_is_valid = (phone) => {
    const phoneRegex = /^[\s()+-]*([0-9][\s()+-]*){6,20}$/;

    return phoneRegex.test(phone);
};

/**
 * Renders the standalone admin page for creating a new lead.
 */
export const NEW_LEAD = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { lang } = useParams();
    const { alerts, setAlerts } = useContext(AlertContext);
    const routeLanguage = lang || "en";
    const [sidebar, setSidebar] = useState(true);
    const [lead, setLead] = useState(initialLead);
    const [emailInput, setEmailInput] = useState("");
    const [phoneInput, setPhoneInput] = useState("");
    const [locationInput, setLocationInput] = useState("");
    const [emailMessages, setEmailMessages] = useState(initialMessages);
    const [phoneMessages, setPhoneMessages] = useState(initialMessages);
    const [saving, setSaving] = useState(false);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const firstNameErrors = saveAttempted && !lead.firstname.trim() ? [t("First name is required")] : [];
    const lastNameErrors = saveAttempted && !lead.lastname.trim() ? [t("Last name is required")] : [];
    const phoneRequiredErrors = saveAttempted && !lead.phones.length && !phoneInput.trim()
        ? [t("Phone number is required")]
        : [];
    const emailErrors = [...emailMessages.errors];
    const phoneErrors = [...phoneRequiredErrors, ...phoneMessages.errors];
    const businessCategories = get_business_categories(t);
    const categoryOptions = Object.keys(businessCategories);
    const subcategoryOptions = businessCategories[lead.category] || [];

    /**
     * Updates one simple lead field from an Alexandria input.
     */
    const update_lead_field = (field, value) => {
        if (field === "category") {
            setLead({
                ...lead,
                category: value,
                subcategory: "",
            });
            return;
        }

        if (field === "subcategory") {
            setLead({
                ...lead,
                subcategory: value,
            });
            return;
        }

        if (field === "brand") {
            setLead({
                ...lead,
                brand: read_brand_input_value(value)
            });
            return;
        }

        setLead({
            ...lead,
            [field]: value
        });
    };

    /**
     * Adds one Alexandria list input value to the requested lead array.
     */
    const add_list_value = (field, value, setInputValue) => {
        const cleanValue = value.trim();

        if (!cleanValue) {
            return;
        }

        setLead({
            ...lead,
            [field]: [...lead[field], cleanValue]
        });
        setInputValue("");
    };

    /**
     * Validates the email input and prepares Alexandria helper messages.
     */
    const validate_email = (email) => {
        const cleanEmail = email.trim().toLowerCase();
        const helpers = cleanEmail && !read_email_is_valid(cleanEmail) ? [t("Please enter a valid email")] : [];
        const successes = cleanEmail && read_email_is_valid(cleanEmail) ? [t("Email is valid")] : [];

        setEmailInput(cleanEmail);
        setEmailMessages({
            successes,
            helpers,
            errors: []
        });
    };

    /**
     * Validates the phone input and prepares Alexandria helper messages.
     */
    const validate_phone = (phone) => {
        const helpers = phone && !read_phone_is_valid(phone) ? [t("Please enter a valid phone number")] : [];
        const successes = phone && read_phone_is_valid(phone) ? [t("Phone number is valid")] : [];

        setPhoneInput(phone);
        setPhoneMessages({
            successes,
            helpers,
            errors: []
        });
    };

    /**
     * Adds a valid email to the list and moves helper text into errors when invalid.
     */
    const add_email_value = () => {
        const cleanEmail = emailInput.trim().toLowerCase();

        if (!cleanEmail) {
            return;
        }

        if (!read_email_is_valid(cleanEmail)) {
            setEmailMessages({
                successes: [],
                helpers: [],
                errors: [t("Please enter a valid email")]
            });
            return;
        }

        add_list_value("emails", cleanEmail, setEmailInput);
        setEmailMessages(initialMessages);
    };

    /**
     * Adds a valid phone to the list and moves helper text into errors when invalid.
     */
    const add_phone_value = () => {
        const cleanPhone = phoneInput.replace(/\s+/g, "");

        if (!cleanPhone) {
            return;
        }

        if (!read_phone_is_valid(cleanPhone)) {
            setPhoneMessages({
                successes: [],
                helpers: [],
                errors: [t("Please enter a valid phone number")]
            });
            return;
        }

        add_list_value("phones", cleanPhone, setPhoneInput);
        setPhoneMessages(initialMessages);
    };

    /**
     * Adds one location to the Alexandria list input.
     */
    const add_location_value = () => {
        add_list_value("locations", locationInput, setLocationInput);
    };

    /**
     * Removes one Alexandria list input value from the requested lead array.
     */
    const delete_list_value = (field, index) => {
        setLead({
            ...lead,
            [field]: lead[field].filter((item, itemIndex) => itemIndex !== index)
        });
    };

    /**
     * Sends the new prospect payload through the new Niffler Prospect API.
     */
    const save_new_lead = async () => {
        setSaveAttempted(true);

        if (!lead.firstname.trim() || !lead.lastname.trim() || (!lead.phones.length && !phoneInput.trim())) {
            alertMessage(alerts, setAlerts, t("Fill in the required lead info"), "Error");
            return;
        }

        if (emailInput.trim() && !read_email_is_valid(emailInput.trim().toLowerCase())) {
            setEmailMessages({
                successes: [],
                helpers: [],
                errors: [t("Please enter a valid email")]
            });
            return;
        }

        if (phoneInput.trim() && !read_phone_is_valid(phoneInput.replace(/\s+/g, ""))) {
            setPhoneMessages({
                successes: [],
                helpers: [],
                errors: [t("Please enter a valid phone number")]
            });
            return;
        }

        setSaving(true);

        const nameParts = read_name_parts(lead.firstname, lead.lastname);
        const emails = read_list_with_pending_value(lead.emails, emailInput.toLowerCase());
        const phones = read_list_with_pending_value(lead.phones, phoneInput.replace(/\s+/g, ""));
        const locations = read_list_with_pending_value(lead.locations, locationInput);
        const loggedUser = store.getState()?.user?.user;
        const submittedByName = loggedUser?.firstname;
        const source = lead.platformSource.trim() || read_submitter_source(loggedUser);
        const payload = {
            firstName: nameParts.firstname,
            lastName: nameParts.lastname,
            source,
            brands: [
                {
                    ownerFirstName: nameParts.firstname,
                    ownerLastName: nameParts.lastname,
                    name: read_brand_input_value(lead.brand),
                    category: lead.category.trim(),
                    subcategory: lead.subcategory.trim(),
                    location: locations.join(", "),
                    emails,
                    phones,
                    paidStatus: "none",
                    status: "Backlog"
                }
            ]
        };
        const actions = read_extra_info_actions(lead.extraInfo, submittedByName);
        const response = await add_prospect(payload, actions).catch(() => false);
        const responseCode = response?.data?.code;

        setSaving(false);

        if (responseCode === "SHARED_CONTACT_CANCELLED") return;

        if (responseCode === "T4000") {
            alertMessage(alerts, setAlerts, t("Prospect with the same account identity already exists"), "Error");
            return;
        }

        if (responseCode !== "T2003") {
            alertMessage(alerts, setAlerts, t("There was an error. Please try again"), "Error");
            return;
        }

        setLead(initialLead);
        setEmailInput("");
        setPhoneInput("");
        setLocationInput("");
        setEmailMessages(initialMessages);
        setPhoneMessages(initialMessages);
        setSaveAttempted(false);
        alertMessage(alerts, setAlerts, t("Prospect added successfully"), "Success");
    };

    return (
        <NewLead>
            <Alert_ duration={6000} />
            <Toolbar_
                sidebar={sidebar}
                onSidebar={() => setSidebar(!sidebar)}
                routeLanguage={routeLanguage}
                onInternalNavigate={navigate}
            />
            <PageSidebar>
                <PageAlwaysVisible>
                    <NewLeadContent>
                        <PageTitle>
                            <h1>{t("New Lead")}</h1>
                        </PageTitle>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t("Fill in lead's info")}</h4>
                            </FormBoxTitle>
                            <Builder_ split>
                                <Input_
                                    required
                                    placeholder={t("First name")}
                                    value={lead.firstname}
                                    onChange={(event) => update_lead_field("firstname", event.target.value)}
                                    errors={firstNameErrors}
                                />
                                <Input_
                                    required
                                    placeholder={t("Last name")}
                                    value={lead.lastname}
                                    onChange={(event) => update_lead_field("lastname", event.target.value)}
                                    errors={lastNameErrors}
                                />
                            </Builder_>
                            <Builder_>
                                <Input_
                                    placeholder={t("Email")}
                                    value={emailInput}
                                    list={lead.emails}
                                    icon_ListItem="Email"
                                    add={t("Add")}
                                    onChange={(event) => validate_email(event.target.value)}
                                    onAdd={add_email_value}
                                    onDelete={(index) => delete_list_value("emails", index)}
                                    helpers={emailMessages.helpers}
                                    successes={emailMessages.successes}
                                    errors={emailErrors}
                                />
                            </Builder_>
                            <Builder_ >
                                <Input_
                                    required
                                    placeholder={t("Phone number")}
                                    value={phoneInput}
                                    list={lead.phones}
                                    icon_ListItem="Phone"
                                    add={t("Add")}
                                    onChange={(event) => validate_phone(event.target.value)}
                                    onAdd={add_phone_value}
                                    onDelete={(index) => delete_list_value("phones", index)}
                                    helpers={phoneMessages.helpers}
                                    successes={phoneMessages.successes}
                                    errors={phoneErrors}
                                />
                            </Builder_>
                            <Builder_ last>
                                <Textarea_
                                    placeholder={t("Extra info")}
                                    value={lead.extraInfo}
                                    onChange={(event) => update_lead_field("extraInfo", event.target.value)}
                                />
                            </Builder_>
                        </FormBox>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t("Additional info")}</h4>
                            </FormBoxTitle>
                            <Builder_>
                                <Input_
                                    placeholder={t("Brand name")}
                                    value={lead.brand}
                                    onChange={(event) => update_lead_field("brand", event.target.value)}
                                />
                            </Builder_>
                            <Builder_ split>
                                <Dropdown_ label={t("Category")}>
                                    <select
                                        value={lead.category}
                                        onChange={(event) => update_lead_field("category", event.target.value)}
                                    >
                                        <option value="">{t("Category")}</option>
                                        {categoryOptions.map((category) => (
                                            <option key={category} value={category}>
                                                {category}
                                            </option>
                                        ))}
                                    </select>
                                </Dropdown_>
                                <Dropdown_ label={t("Subcategory")}>
                                    <select
                                        value={lead.subcategory}
                                        onChange={(event) => update_lead_field("subcategory", event.target.value)}
                                    >
                                        <option value="">{t("Subcategory")}</option>
                                        {subcategoryOptions.map((subcategory) => (
                                            <option key={subcategory} value={subcategory}>
                                                {subcategory}
                                            </option>
                                        ))}
                                    </select>
                                </Dropdown_>
                            </Builder_>
                            <Builder_>
                                <Location_Input
                                    placeholder={t("Location")}
                                    value={locationInput}
                                    list={lead.locations}
                                    icon_ListItem="Bullet"
                                    add={t("Add")}
                                    onChange={(event) => setLocationInput(event.target.value)}
                                    onAdd={add_location_value}
                                    onDelete={(index) => delete_list_value("locations", index)}
                                />
                            </Builder_>
                            
                        </FormBox>
                        <Actions_>
                            <Grey_
                                text={t("Exit")}
                                iconLeft="Xicon"
                                onClick={() => navigate(`/${routeLanguage}/`)}
                            />
                            <Main_
                                text={saving ? t("Saving") : t("Save")}
                                iconLeft="Save"
                                onClick={save_new_lead}
                            />
                        </Actions_>
                    </NewLeadContent>
                </PageAlwaysVisible>
            </PageSidebar>
        </NewLead>
    );
};
